import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { API } from '../config/api';

/**
 * Hook to fetch active announcements and handle dismissals.
 * Combines server-side tracking (logged-in) with localStorage (guests).
 */
export function useAnnouncements() {
  const { token, isAuthenticated } = useAuth();
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAnnouncements = useCallback(async () => {
    try {
      const headers = {};
      if (token) headers.Authorization = `Bearer ${token}`;
      const res = await fetch(`${API}/announcements/active`, { headers });
      if (res.ok) {
        const data = await res.json();
        let anns = data.announcements || [];

        // For guests, filter out locally dismissed
        if (!isAuthenticated) {
          const dismissed = JSON.parse(localStorage.getItem('dismissed_announcements') || '[]');
          anns = anns.filter(a => !dismissed.includes(a.announcement_id));
        }

        setAnnouncements(anns);
      }
    } catch {
      // Silently fail — announcements are non-critical
    } finally {
      setLoading(false);
    }
  }, [token, isAuthenticated]);

  useEffect(() => {
    fetchAnnouncements();
  }, [fetchAnnouncements]);

  const dismiss = useCallback(async (announcementId) => {
    // Optimistic update
    setAnnouncements(prev => prev.filter(a => a.announcement_id !== announcementId));

    if (isAuthenticated && token) {
      try {
        await fetch(`${API}/announcements/${announcementId}/dismiss`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch { /* no-op */ }
    } else {
      const dismissed = JSON.parse(localStorage.getItem('dismissed_announcements') || '[]');
      if (!dismissed.includes(announcementId)) {
        dismissed.push(announcementId);
        localStorage.setItem('dismissed_announcements', JSON.stringify(dismissed));
      }
    }
  }, [isAuthenticated, token]);

  return { announcements, loading, dismiss, refresh: fetchAnnouncements };
}
